import React from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import { Participant } from '../types';
import VideoStream from './VideoStream';

interface VideoGridProps {
  participants: Participant[];
  mainPresenter: Participant | null;
  onToggleVideo: (participantId: string) => void;
  onToggleAudio: (participantId: string) => void;
  currentUserId: string;
  playbackEnabled?: boolean;
}

const VideoGrid = ({
  participants,
  mainPresenter,
  onToggleVideo,
  onToggleAudio,
  currentUserId,
  playbackEnabled = false,
}: VideoGridProps) => {
  const otherParticipants = participants.filter((p) => p.id !== mainPresenter?.id);

  return (
    <div className="flex flex-col h-full gap-4">
      {mainPresenter && (
        <div className="relative flex-1 bg-slate-900 rounded-lg overflow-hidden">
          <div className="absolute inset-0 flex items-center justify-center">
            {mainPresenter.isVideoOn && mainPresenter.stream ? (
              <VideoStream
                stream={mainPresenter.stream}
                muted={mainPresenter.id === currentUserId || !playbackEnabled}
                className="w-full h-full object-cover"
              />
            ) : mainPresenter.isVideoOn && mainPresenter.avatar ? (
              <Image
                src={mainPresenter.avatar}
                alt={`Flux video: ${mainPresenter.name}`}
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="flex flex-col items-center gap-4">
                <div className="w-24 h-24 rounded-full bg-primary flex items-center justify-center">
                  <span className="text-4xl font-semibold text-primary-foreground">
                    {mainPresenter.name.charAt(0).toUpperCase()}
                  </span>
                </div>
                <p className="text-white text-lg font-medium">{mainPresenter.name}</p>
              </div>
            )}
          </div>

          <div className="absolute top-4 left-4 flex items-center gap-2 bg-black/60 px-3 py-2 rounded-md">
            <Icon
              name={mainPresenter.role === 'teacher' || mainPresenter.role === 'admin' ? 'GraduationCap' : 'User'}
              size={16}
              color="white"
            />
            <span className="text-white text-sm font-medium">{mainPresenter.name}</span>
            {mainPresenter.isPresenting && (
              <span className="ml-2 px-2 py-0.5 bg-error text-white text-xs font-medium rounded">
                Prezintă
              </span>
            )}
          </div>

          <div className="absolute bottom-4 right-4 flex items-center gap-2">
            {mainPresenter.id === currentUserId && (
              <>
                <button
                  onClick={() => onToggleAudio(mainPresenter.id)}
                  className={`p-3 rounded-full transition-colors duration-150 ${
                    mainPresenter.isAudioOn ? 'bg-slate-700 hover:bg-slate-600' : 'bg-error hover:bg-error/90'
                  }`}
                  aria-label={mainPresenter.isAudioOn ? 'Dezactivează microfonul' : 'Activează microfonul'}
                >
                  <Icon name={mainPresenter.isAudioOn ? 'Mic' : 'MicOff'} size={20} color="white" />
                </button>
                <button
                  onClick={() => onToggleVideo(mainPresenter.id)}
                  className={`p-3 rounded-full transition-colors duration-150 ${
                    mainPresenter.isVideoOn ? 'bg-slate-700 hover:bg-slate-600' : 'bg-error hover:bg-error/90'
                  }`}
                  aria-label={mainPresenter.isVideoOn ? 'Oprește camera' : 'Pornește camera'}
                >
                  <Icon name={mainPresenter.isVideoOn ? 'Video' : 'VideoOff'} size={20} color="white" />
                </button>
              </>
            )}
          </div>

          {mainPresenter.isHandRaised && (
            <div className="absolute top-4 right-4 bg-warning text-warning-foreground px-3 py-2 rounded-md flex items-center gap-2 animate-pulse">
              <Icon name="Hand" size={16} />
              <span className="text-sm font-medium">Mână ridicată</span>
            </div>
          )}
        </div>
      )}

      {otherParticipants.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-2 h-32">
          {otherParticipants.slice(0, 12).map((participant) => (
            <div key={participant.id} className="relative bg-slate-900 rounded-lg overflow-hidden">
              <div className="absolute inset-0 flex items-center justify-center">
                {participant.isVideoOn && participant.stream ? (
                  <VideoStream
                    stream={participant.stream}
                    muted={participant.id === currentUserId || !playbackEnabled}
                    className="w-full h-full object-cover"
                  />
                ) : participant.isVideoOn && participant.avatar ? (
                  <Image
                    src={participant.avatar}
                    alt={`Miniatură video: ${participant.name}`}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="flex flex-col items-center gap-1">
                    <div className="w-12 h-12 rounded-full bg-primary flex items-center justify-center">
                      <span className="text-lg font-semibold text-primary-foreground">
                        {participant.name.charAt(0).toUpperCase()}
                      </span>
                    </div>
                  </div>
                )}
              </div>

              <div className="absolute bottom-1 left-1 right-1 bg-black/60 px-2 py-1 rounded flex items-center justify-between">
                <span className="text-white text-xs font-medium truncate flex-1">{participant.name}</span>
                <div className="flex items-center gap-1 ml-1">
                  {!participant.isAudioOn && <Icon name="MicOff" size={12} color="white" />}
                  {participant.isHandRaised && <Icon name="Hand" size={12} color="#D97706" />}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default VideoGrid;
